// "Complete your loadout" — PDP add-on picker for Mouse / Keyboard PDPs.
// Mouse: Ice-Flex sleeves + random mouse-skates + random glass mousepad.
// Keyboard: JKLP coiled cable + random HE switch. Picks come from
// /api/complete-loadout; each selected row is added via the normal cart.
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { API_BASE } from '../../lib/apiBase';
import { useCart } from '../../state/cart';

const KIND_BY_TYPE = { mouse: 'mouse', keyboard: 'keyboard' };

export function completeLoadoutKind(productType) {
        return KIND_BY_TYPE[String(productType || '').trim().toLowerCase()] || null;
}

function inr(amount) {
        const n = Number(amount);
        return Number.isFinite(n) ? `₹${n.toLocaleString('en-IN', { maximumFractionDigits: 0 })}` : '';
}

function buyableVariants(p) {
        return (p?.variants?.edges || []).map((e) => e.node).filter((v) => v.availableForSale);
}

function AddonRow({ product, checked, variantId, onToggle, onVariant }) {
        const variants = buyableVariants(product);
        const v = variants.find((x) => x.id === variantId) || variants[0];
        const showCompare = v?.compareAtPrice && Number(v.compareAtPrice.amount) > Number(v.price?.amount);
        const img = v?.image?.url || product.featuredImage?.url || product.images?.[0]?.url;
        return (
                <div data-testid="complete-loadout-row" className="flex items-center gap-3 sm:gap-4 px-4 py-3 border-t border-white/10 first:border-t-0">
                        <input
                                type="checkbox"
                                checked={checked}
                                onChange={onToggle}
                                aria-label={`Select ${product.title}`}
                                data-testid={`complete-loadout-check-${product.handle}`}
                                className="h-4 w-4 shrink-0 accent-[#FF9F1C]"
                        />
                        <div className="h-12 w-12 shrink-0 bg-[#111] border border-white/10 flex items-center justify-center overflow-hidden">
                                {img && <img src={img} alt={product.title} loading="lazy" className="max-h-full max-w-full object-contain" />}
                        </div>
                        <Link to={`/p/${product.handle}`} className="flex-1 min-w-0 font-sans text-[13px] text-white/90 hover:text-[#FF9F1C] transition-colors line-clamp-2">
                                {product.title}
                        </Link>
                        {variants.length > 1 && (
                                <select
                                        value={v?.id}
                                        onChange={(e) => onVariant(e.target.value)}
                                        data-testid={`complete-loadout-variant-${product.handle}`}
                                        className="hidden sm:block max-w-[160px] bg-[#0A0A0A] border border-white/20 text-white font-mono text-[11px] px-2 py-1.5 focus:outline-none focus:border-[#FF9F1C]"
                                >
                                        {variants.map((x) => <option key={x.id} value={x.id}>{x.title}</option>)}
                                </select>
                        )}
                        <div className="shrink-0 text-right">
                                <span className="font-mono text-[13px] text-white tabular-nums">{inr(v?.price?.amount)}</span>
                                {showCompare && (
                                        <span className="block font-mono text-[11px] text-white/40 line-through tabular-nums">{inr(v.compareAtPrice.amount)}</span>
                                )}
                        </div>
                </div>
        );
}

export default function CompleteLoadout({ kind, excludeHandle }) {
        const [products, setProducts] = useState([]);
        const [checked, setChecked] = useState({});
        const [variantOf, setVariantOf] = useState({});
        const [busy, setBusy] = useState(false);
        const [msg, setMsg] = useState('');
        const { addLine } = useCart();

        useEffect(() => {
                let alive = true;
                fetch(`${API_BASE}/api/complete-loadout?type=${kind}&exclude=${encodeURIComponent(excludeHandle || '')}`)
                        .then((r) => r.json())
                        .then((j) => { if (alive) setProducts((j.products || []).filter((p) => buyableVariants(p).length)); })
                        .catch(() => {});
                return () => { alive = false; };
        }, [kind, excludeHandle]);

        const selected = useMemo(() => products.filter((p) => checked[p.handle]), [products, checked]);

        const addSelected = async () => {
                if (!selected.length || busy) return;
                setBusy(true);
                setMsg('');
                let ok = 0;
                for (const p of selected) {
                        const vid = variantOf[p.handle] || buyableVariants(p)[0]?.id;
                        const res = await addLine(vid, { intent: 'buy', addedFrom: 'pdp.complete-loadout.v1' });
                        if (res?.ok) ok += 1;
                }
                setBusy(false);
                setMsg(ok === selected.length ? `Added ${ok} item${ok > 1 ? 's' : ''} to cart` : 'Some items could not be added — try again');
                if (ok) setChecked({});
                setTimeout(() => setMsg(''), 4000);
        };

        if (!products.length) return null;
        return (
                <section data-testid="complete-loadout-section" className="mt-10 border border-white/10 bg-white/[0.02]">
                        <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-white/10">
                                <p className="font-mono text-[11px] tracking-[0.18em] uppercase text-[#FF9F1C]">Complete your loadout</p>
                                {msg && <p data-testid="complete-loadout-message" className="font-mono text-[10px] tracking-[0.14em] uppercase text-white/70">{msg}</p>}
                        </div>
                        {products.map((p) => (
                                <AddonRow
                                        key={p.handle}
                                        product={p}
                                        checked={!!checked[p.handle]}
                                        variantId={variantOf[p.handle]}
                                        onToggle={() => setChecked((c) => ({ ...c, [p.handle]: !c[p.handle] }))}
                                        onVariant={(id) => setVariantOf((m) => ({ ...m, [p.handle]: id }))}
                                />
                        ))}
                        <div className="px-4 py-3 border-t border-white/10 flex justify-end">
                                <button
                                        type="button"
                                        onClick={addSelected}
                                        disabled={!selected.length || busy}
                                        data-testid="complete-loadout-add-button"
                                        className="font-mono text-[11px] tracking-[0.2em] uppercase px-5 py-2.5 border border-[#FF9F1C] bg-[#FF9F1C] text-[#0A0A0A] hover:bg-transparent hover:text-[#FF9F1C] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                                >
                                        {busy ? 'Adding…' : selected.length ? `Add ${selected.length} selected to cart` : 'Select items to add'}
                                </button>
                        </div>
                </section>
        );
}
